import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

import { Quake, AgeColors } from '../model/quake';

import { ErrorHandleService } from './error-handle.service';
import { EventQueryParameters } from '../model/event-query-parameters';
import { StorageMap } from '@ngx-pwa/local-storage';
import { SettingsService } from './settings.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class EventsService {
  protected SERVICE_URL = 'https://service.iris.edu/fdsnws/event/1/query';
  protected CONNECTION_TIMEOUT = 30000;
  protected MAX_EVENTS = 100000;
  protected events: Quake[] = [];
  protected httpOptions: any = {};
  public lastUpdated = new Date();
  public latestEvents = []
  public status = [];
  // private serviceUrl = 'https://earthquake.usgs.gov/fdsnws/event/1/query?';

  constructor(
    public http: HttpClient,
    protected errorHandleService: ErrorHandleService,
    protected storage: StorageMap,
    protected settingsService: SettingsService,
    protected translationService: TranslateService
  ) {
    const { lastUpdated, events } = this.settingsService.fromStorage;
    this.init(lastUpdated, events);
  }

  public init(lastUpdated: string, eventsRaw: Quake[]): void {
    this.status.push(this.translationService.instant('loading.fetch_local'));
    if (lastUpdated && Array.isArray(eventsRaw)) {
      this.lastUpdated = new Date(lastUpdated);
      this.events = eventsRaw.map(row => Quake.fromObject(row));
      this.status.push(this.translationService.instant('loading.fetch_local_found', { value: this.events.length.toLocaleString() }));
    }
  }

  public getColorFromAge(quake: Quake): string | null {
    if (!quake) { return null; }
    const colorIndex = quake.getEventColorIndex();
    return this.settingsService.settings.getColor(colorIndex);
  }

  public getMagnitudePixels(quake: Quake): number {
    const multiFactor = window.screen.height / 1080;
    return Math.max(quake.magnitude * quake.magnitude, 5) * 2 * multiFactor;
  }

  public getMagStrikeWidth(quake: Quake): number {
    const mag = Math.floor(quake.magnitude);
    return Math.max(mag - 1, 2);
  }

  protected filterEvents(quakes: Array<Quake>, offset = 0, whileComparator = (quake: Quake): boolean => true) {
    const res = [];
    let quake = quakes[offset];
    while (quake && whileComparator(quake)) {
      res.push(quake);
      quake = quakes[offset++];
    }
    return res;
  }

  public filterLastHourEvents(quakes: Array<Quake>): Array<Quake> {
    return this.filterEvents(quakes, 0, quake => quake.getEventColorIndex() === AgeColors.HOUR);
  }

  public filterLastMonthEvents(quakes: Array<Quake>, lastHourEventsLength = 0): Array<Quake> {
    return this.filterEvents(quakes, lastHourEventsLength, quake => {
      const color = quake.getEventColorIndex();
      return AgeColors.DAY === color || AgeColors.WEEK === color || AgeColors.MONTH === color;
    });
  }

  protected shouldFetchEvents(): boolean {
    return (Date.now() - this.lastUpdated.getTime() > this.settingsService.settings.dataFetchInterval) || this.events.length === 0;
  }

  public async clearStored() {
    this.events = [];
    await lastValueFrom(this.storage.delete('eventsWorld'));
    await lastValueFrom(this.storage.delete('lastUpdated'));
  }

  public getEvents(options: EventQueryParameters): Observable<Quake[]> {
    if (this.shouldFetchEvents()) {
      const url = this.getEndpointURL(options);
      this.lastUpdated = new Date();

      return this.http.get(url, this.httpOptions).pipe(
        timeout(this.CONNECTION_TIMEOUT),
        map((res) => {
          const newEvents = this.extractData(res);
          this.status.push(this.translationService.instant('loading.fetch_server_found', { value: newEvents.length.toLocaleString() }));

          const t = Date.now();
          let duplicates = 0;

          if (this.events.length === 0) {
            this.events = newEvents;
          } else {
            this.latestEvents = [];
            const headID = this.events[0].id;
            newEvents.forEach((e, i) => {
              const index = this.events.findIndex(oe => oe.id === e.id);
              if (index === -1) {
                if (e.id > headID) {
                  this.latestEvents.push(e);
                }
                this.events.unshift(e);
              } else {
                this.events[index] = e;
                duplicates += 1;
              }
            });
            this.events.sort((a, b) => a.time.getTime() < b.time.getTime() ? 1 : -1);
            if (this.events.length > this.MAX_EVENTS) {
              this.events.splice(this.MAX_EVENTS);
            }
          }
          console.log([
            `Sorting and merging took ${Date.now() - t} ms.`,
            `${newEvents.length} events fetched`,
            `${this.events.length - (newEvents.length - duplicates)} prior to fetch in the db`,
            `${duplicates} duplicates, `,
            `${this.events.length} new count`,
            `${this.latestEvents.length} NEW events`,
            `${newEvents.length - duplicates} diff (old+new)`,
          ].join('\n\t'));

          this.storage.set('eventsWorld', this.events).subscribe(() => console.log('Writing to DB: eventsWorld'));
          this.storage.set('lastUpdated', Date.now()).subscribe(() => console.log('Writing to DB: lastUpdated'));
          return this.events;
        }),
        catchError(this.errorHandleService.httpGet('getEvents', this.events))
      );
    } else {
      return of(this.events);
    }
  }

  protected extractData(res: any): Quake[] { return [] }

  protected getEndpointURL(options): string { return '' }
}

export class IRISEventsService extends EventsService {
  constructor(
    public http: HttpClient,
    protected errorHandleService: ErrorHandleService,
    protected storage: StorageMap,
    protected settingsService: SettingsService,
    protected translationService: TranslateService
  ) {
    super(http, errorHandleService, storage, settingsService, translationService)
    this.SERVICE_URL = 'https://service.iris.edu/fdsnws/event/1/query';
    this.httpOptions.responseType = "text"
  }

  protected extractData(res: string): Quake[] {
    const quakeList = res.trim().split('\n');
    quakeList.shift();
    return quakeList.map(s => new Quake().fromString(s));
  }

  protected getEndpointURL(options): string {
    options.updatedafter = this.lastUpdated.toISOString();
    if (this.events.length === 0) {
      const d = new Date();
      d.setFullYear(this.lastUpdated.getFullYear() - 1);
      options.starttime = d.toISOString();
      delete options.updatedafter;
      this.status.push(this.translationService.instant('loading.fetch_server'));
    }
    return this.SERVICE_URL + options.serialize();
  }
}

export class USGSEventsService extends EventsService {
  constructor(
    public http: HttpClient,
    protected errorHandleService: ErrorHandleService,
    protected storage: StorageMap,
    protected settingsService: SettingsService,
    protected translationService: TranslateService
  ) {
    super(http, errorHandleService, storage, settingsService, translationService)
  }

  protected extractData(res: any): Quake[] {
    return res.features.map(r => {
      const q = new Quake();
      const [longitude, latitude, depth] = r.geometry.coordinates
      const prop = r.properties;
      q.id = r.id
      q.time = new Date(prop.time)
      q.depth = depth
      q.latitude = latitude;
      q.longitude = longitude;
      q.magnitude = prop.mag;
      q.eventLocationName = prop.place
      return q;
    });
  }

  protected getEndpointURL(options): string {
    let url = 'https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/2.5_hour.geojson';
    if (this.events.length === 0) {
      url = 'https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/2.5_month.geojson';
      this.status.push(this.translationService.instant('loading.fetch_server'));
    }
    return url;
  }
}
